import { useEffect, useState, useCallback } from 'react';
import { useAuthContext } from '../auth/useAuthContext';
import { getStorage } from '../../utilities/helpers/storage';
import { StorageKey, StorageType } from '../../utilities/constants/common';
import { EditorPopupId } from '../../utilities/constants/popup-type';

type IsOpenPopupsState = Record<EditorPopupId, boolean>;

export const useEditorPopups = () => {
  const { isUserSubscribed, freePremiumGenerationsAttemptsCount } =
    useAuthContext();

  const justUsedFreeSmartAnswer =
    getStorage(StorageType.LOCAL).getItem(
      StorageKey.JUST_USED_FREE_SMART_ANSWER
    ) === 'true';

  const [isOpenPopups, setIsOpenPopups] = useState<IsOpenPopupsState>({
    [EditorPopupId.GEN_ONE_PREMIUM_AI_RESPONSE]: false,
    [EditorPopupId.PREMIUM_ANSWER_USED]: false,
    [EditorPopupId.GOOGLE_ADSENSE]: false,
    [EditorPopupId.GEN_FREE_AI_RESPONSE]: false,
    [EditorPopupId.GEN_PREMIUM_AI_RESPONSE]: false,
  });

  const handleSetIsOpenPopup = useCallback(
    (popupId: EditorPopupId, isOpen: boolean) => {
      setIsOpenPopups((prev) => ({ ...prev, [popupId]: isOpen }));
    },
    []
  );

  useEffect(() => {
    if (justUsedFreeSmartAnswer && isOpenPopups.premiumAnswerUsed) {
      getStorage(StorageType.LOCAL).removeItem(
        StorageKey.JUST_USED_FREE_SMART_ANSWER
      );
    }
  }, [justUsedFreeSmartAnswer, isOpenPopups.premiumAnswerUsed]);

  const handleInitEditorPopups = useCallback(
    (shouldOpenPopup: boolean = false) => {
      if (
        !isUserSubscribed &&
        freePremiumGenerationsAttemptsCount >= 1 &&
        shouldOpenPopup
      ) {
        handleSetIsOpenPopup(EditorPopupId.GEN_ONE_PREMIUM_AI_RESPONSE, true);
        return;
      }

      if (!isUserSubscribed && justUsedFreeSmartAnswer) {
        handleSetIsOpenPopup(EditorPopupId.PREMIUM_ANSWER_USED, true);
        return;
      }

      if (!isUserSubscribed && shouldOpenPopup) {
        handleSetIsOpenPopup(EditorPopupId.GOOGLE_ADSENSE, true);
        return;
      }

      if (isUserSubscribed && shouldOpenPopup) {
        handleSetIsOpenPopup(EditorPopupId.GEN_PREMIUM_AI_RESPONSE, true);
      }
    },
    [
      isUserSubscribed,
      justUsedFreeSmartAnswer,
      freePremiumGenerationsAttemptsCount,
      handleSetIsOpenPopup,
    ]
  );

  return {
    isOpenPopups,
    justUsedFreeSmartAnswer,
    handleSetIsOpenPopup,
    handleInitEditorPopups,
  };
};
